import React, { useState, useCallback, useEffect } from 'react'

import { PaymentIframe3DS } from '@mindfulchefuk/forms/CardField/PaymentIframe3DS'
import { Modal } from '@mindfulchefuk/design-system/Modal'
import { isAuthenticated } from '@mindfulchefuk/helpers/js/authentication'

import usePrevious from '@mindfulchefuk/hooks/usePrevious'

import {
  trackErrorMakePaymentData,
  trackUseChallengeURL,
  trackCompleteChallengeURL,
  trackCompleteWith3DS,
  trackErrorWithVerifyPayment,
  trackVerifyPayment,
} from '@mindfulchefuk/forms/CardField/utils/tracking'

import {
  verifyPaymentAnonymous,
  verifyPayment,
} from '@mindfulchefuk/forms/CardField/services/verifyPayments/verifyPayments'

import {
  SecurePaymentState,
  SecurePaymentStage,
} from '@mindfulchefuk/forms/CardField/SecurePayment.types'
import { CARD_FAILED_TEXT } from '@mindfulchefuk/features/Notifications/constants'

const CHECKOUT_SESSION_ID = 'cko-session-id'

type TCheck3dsModal = {
  redirectUrl: string
  onSuccess: () => void
  onError: (error: Error) => void
}

export const Check3dsModal = ({
  redirectUrl,
  onSuccess,
  onError,
}: TCheck3dsModal) => {
  const [showSecurePayment, setShowSecurePayment] = useState<boolean>(false)

  const onSecurePaymentComplete = useCallback(() => {
    setShowSecurePayment(false)
    onSuccess()
  }, [onSuccess])

  const onSecurePaymentError = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error: any) => {
      setShowSecurePayment(false)
      onError(error)
    },
    [onError]
  )

  const [paymentState, setPaymentState] = useState<SecurePaymentState>({
    stage: SecurePaymentStage.MAKING_PAYMENT,
  })

  const verifyPaymentHandler = useCallback((href: string) => {
    const checkoutSessionId = new URL(href).searchParams.get(
      CHECKOUT_SESSION_ID
    )
    return isAuthenticated()
      ? verifyPayment(checkoutSessionId)
      : verifyPaymentAnonymous(checkoutSessionId)
  }, [])

  const verifyPaymentSessionID = async (href: string) => {
    if (href.includes(CHECKOUT_SESSION_ID)) {
      trackCompleteChallengeURL()
      setPaymentState({ stage: SecurePaymentStage.VERIFY_PAYMENT })
      trackVerifyPayment()
      try {
        await verifyPaymentHandler(href)
        trackCompleteWith3DS()
        onSecurePaymentComplete()
      } catch (error) {
        trackErrorWithVerifyPayment(error)
        onSecurePaymentError(error)
      }
      setPaymentState({ stage: SecurePaymentStage.COMPLETE })
    }
  }

  const previousRedirectUrl = usePrevious(redirectUrl)

  useEffect(() => {
    const submit3DSPayment = async () => {
      if (redirectUrl !== previousRedirectUrl) {
        if (redirectUrl) {
          // Successful with 3D Secure challenge url
          trackUseChallengeURL()
          setPaymentState({
            stage: SecurePaymentStage.THREE_D_SECURE,
            url: redirectUrl,
          })
          setShowSecurePayment(true)
        } else {
          trackErrorMakePaymentData({ error: 'missing redirect url' }) // should happen if the card is not enrolled in SCA, which should be extremely rare.
          onSecurePaymentError(new Error(CARD_FAILED_TEXT))
        }
      }
    }

    if (typeof redirectUrl !== 'undefined') {
      submit3DSPayment()
    }
  }, [
    onSecurePaymentComplete,
    onSecurePaymentError,
    previousRedirectUrl,
    redirectUrl,
  ])

  return showSecurePayment ? (
    <Modal size="medium" transparent>
      <PaymentIframe3DS
        verifyPaymentSessionID={verifyPaymentSessionID}
        paymentState={paymentState}
      />
    </Modal>
  ) : null
}
